import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { getDetectionById } from '@/services/detectionService';
import { listItems } from '@/services/itemService';
import { listBranches } from '@/services/branchService';
import { listScanners } from '@/services/scannerService';
import { listShelves } from '@/services/shelfService';
import type {
  Detection,
  Item,
  Scanner,
  Branch,
  Shelf,
} from '@packages/shared-types';

export function DetectionDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detailTitle = useIndustryTranslation('pages.detections.detail.title');
  const description = useIndustryTranslation('pages.detections.description');
  const loadingLabel = useIndustryTranslation('pages.detections.list.loading');
  const errorLoad = useIndustryTranslation('pages.detections.error.load');
  const notFoundLabel = useIndustryTranslation('pages.detections.detail.notFound');

  const [detection, setDetection] = useState<Detection | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [scanners, setScanners] = useState<Scanner[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    setDetection(null);
    try {
      const [detectionData, itemsData, scannersData, branchesData, shelvesData] =
        await Promise.all([
          getDetectionById(id),
          listItems(),
          listScanners(),
          listBranches(),
          listShelves(),
        ]);
      setDetection(detectionData);
      setItems(itemsData);
      setScanners(scannersData);
      setBranches(branchesData);
      setShelves(shelvesData);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : errorLoad;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [id, errorLoad]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '—';
    return new Date(date).toLocaleString();
  };

  const getScanTypeLabel = (scanType: string): string =>
    t(`pages.items.details.scanTypes.${scanType}`, { defaultValue: scanType });

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={detailTitle}
          description={description}
          onBack={() => navigate(-1)}
        />
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-6">
            <LoadingLogo size="sm" />
            <span className="text-sm text-muted-foreground">{loadingLabel}</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !detection) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={detailTitle}
          description={description}
          onBack={() => navigate(-1)}
        />
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">
              {error ?? notFoundLabel}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const item = items.find((i) => i.id === detection.itemId);
  const scanner = scanners.find((s) => s.id === detection.scannerId);
  const branch = branches.find((b) => b.id === detection.branchId);
  const shelf = detection.shelfId
    ? shelves.find((s) => s.id === detection.shelfId)
    : null;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={detailTitle}
        description={description}
        onBack={() => navigate(-1)}
      />

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t('pages.detections.detail.detectedAt')}
              </label>
              <p className="mt-1 text-sm">
                {formatDate(detection.detectedAt)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t('pages.detections.detail.item')}
              </label>
              <div className="mt-1 text-sm">
                {item ? (
                  <Link
                    to={`/items/${detection.itemId}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {item.name}
                    {item.sku ? ` (${item.sku})` : ''}
                  </Link>
                ) : (
                  '—'
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t('pages.detections.detail.scanner')}
              </label>
              <p className="mt-1 text-sm">{scanner?.name ?? '—'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t('pages.detections.detail.branch')}
              </label>
              <p className="mt-1 text-sm">{branch?.name ?? '—'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t('pages.detections.detail.shelf')}
              </label>
              <p className="mt-1 text-sm">{shelf?.name ?? '—'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t('pages.detections.detail.scanType')}
              </label>
              <p className="mt-1 text-sm">
                {getScanTypeLabel(detection.scanType)}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t('pages.detections.detail.rfidTag')}
              </label>
              <p className="mt-1 font-mono text-sm">{detection.rfidTagId}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                {t('pages.detections.detail.signalStrength')}
              </label>
              <p className="mt-1 text-sm">
                {detection.signalStrength != null
                  ? `${detection.signalStrength} dBm`
                  : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
